import React, { useEffect, useState } from "react";

import { ticketsService, practiceTicketsService } from "api/services/tickets.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import { Input, Select } from "components/UI/Input";
import Table, { Column } from "components/UI/Table";
import Tabs, { TabItem } from "components/UI/Tabs";
import { extractList, getApiErrorMessage } from "utils/api-error";

import type { Ticket, PracticeTicket } from "api/types/tickets.types";
import "pages/AdminPage.scss";
import "./TicketList.scss";

const TABS: TabItem[] = [
  { key: "lesson", label: "레슨 티켓" },
  { key: "practice", label: "연습 티켓" },
];

const STATUS_BADGE: Record<string, "active" | "pending" | "rejected" | "draft" | "info"> = {
  UNUSED: "info",
  PARTIALLY_USED: "pending",
  FULLY_USED: "active",
  EXPIRED: "draft",
  CANCELLED: "rejected",
};

const TicketList: React.FC = () => {
  const [activeTab, setActiveTab] = useState("lesson");
  const [lessonTickets, setLessonTickets] = useState<Ticket[]>([]);
  const [practiceTickets, setPracticeTickets] = useState<PracticeTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const fetchLessonTickets = async () => {
    setLoading(true);
    setError("");
    try {
      const params: Record<string, unknown> = {};
      if (statusFilter) params.status = statusFilter;
      const res = await ticketsService.list(params);
      setLessonTickets(extractList<Ticket>(res.data));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchPracticeTickets = async () => {
    setLoading(true);
    setError("");
    try {
      const params: Record<string, unknown> = {};
      const res = await practiceTicketsService.list(params);
      setPracticeTickets(extractList<PracticeTicket>(res.data));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "lesson") {
      fetchLessonTickets();
    } else {
      fetchPracticeTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, statusFilter]);

  const lessonColumns: Column<Ticket>[] = [
    { key: "user_id", title: "사용자", width: "120px" },
    { key: "lesson_product_name", title: "강습 상품", width: "200px" },
    {
      key: "sessions_total",
      title: "세션",
      width: "120px",
      render: (v, row) => `${row.sessions_used}/${v} (잔여 ${row.sessions_remaining})`,
    },
    {
      key: "status",
      title: "상태",
      width: "90px",
      render: (v, row) => <Badge variant={STATUS_BADGE[String(v)] ?? "draft"}>{row.status_display}</Badge>,
    },
    {
      key: "is_active",
      title: "활성",
      width: "60px",
      render: (v) => (v ? <Badge variant="active">활성</Badge> : <Badge variant="draft">비활성</Badge>),
    },
    {
      key: "valid_until",
      title: "만료일",
      width: "110px",
      render: (v) => (v ? String(v).slice(0, 10) : "-"),
    },
    {
      key: "lesson_progress",
      title: "진행도",
      width: "150px",
      render: (v) => {
        const progress = v as Ticket["lesson_progress"];
        if (!progress) return "-";
        const days = progress.completed_days.length > 0 ? `Day ${progress.completed_days.join(", ")}` : "-";
        const theory = progress.theory_completed ? "이론O" : "이론X";
        return `${days} / ${theory}`;
      },
    },
    {
      key: "created_at",
      title: "생성일",
      width: "110px",
      render: (v) => String(v).slice(0, 10),
    },
  ];

  const practiceColumns: Column<PracticeTicket>[] = [
    { key: "user_id", title: "사용자", width: "120px" },
    {
      key: "sessions_total",
      title: "세션",
      width: "150px",
      render: (v, row) => `${row.sessions_used}/${v} (잔여 ${row.remaining_sessions})`,
    },
    {
      key: "is_active",
      title: "활성",
      width: "60px",
      render: (v) => (v ? <Badge variant="active">활성</Badge> : <Badge variant="draft">비활성</Badge>),
    },
    {
      key: "is_valid",
      title: "유효",
      width: "60px",
      render: (v) => (v ? <Badge variant="active">유효</Badge> : <Badge variant="rejected">만료</Badge>),
    },
    {
      key: "valid_until",
      title: "만료일",
      width: "110px",
      render: (v) => (v ? String(v).slice(0, 10) : "-"),
    },
    {
      key: "created_at",
      title: "생성일",
      width: "110px",
      render: (v) => String(v).slice(0, 10),
    },
  ];

  const filteredLessonTickets = lessonTickets.filter((t) => {
    if (!search) return true;
    return t.user_id.toLowerCase().includes(search.toLowerCase()) || t.lesson_product_name.toLowerCase().includes(search.toLowerCase());
  });

  const filteredPracticeTickets = practiceTickets.filter((t) => {
    if (!search) return true;
    return t.user_id.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="admin-page">
      <ContentHeader title="티켓 관리" description="레슨 티켓과 연습 티켓을 통합 관리합니다." />

      <div className="admin-page__card">
        <Tabs
          items={TABS}
          activeKey={activeTab}
          onChange={(k) => {
            setActiveTab(k);
            setStatusFilter("");
            setSearch("");
          }}
        />

        <div className="admin-page__filter">
          <Input placeholder="사용자 ID, 상품명 검색" value={search} onChange={(e) => setSearch(e.target.value)} prefix="🔍" />
          {activeTab === "lesson" && (
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: "", label: "상태 전체" },
                { value: "UNUSED", label: "미사용" },
                { value: "PARTIALLY_USED", label: "일부 사용" },
                { value: "FULLY_USED", label: "완전 사용" },
                { value: "EXPIRED", label: "만료" },
                { value: "CANCELLED", label: "취소됨" },
              ]}
            />
          )}
        </div>

        {error && <div className="admin-page__error">{error}</div>}
        {loading ? <div className="admin-page__loading">불러오는 중...</div> : activeTab === "lesson" ? <Table columns={lessonColumns} data={filteredLessonTickets} rowKey="id" /> : <Table columns={practiceColumns} data={filteredPracticeTickets} rowKey="id" />}
      </div>
    </div>
  );
};

export default TicketList;
